import { styled } from '@linaria/react';
import { Group } from '@mantine/core';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Measure } from '../../../api/work-periods-client/work-periods-client.type.ts';
import { useTranslation } from 'react-i18next';
import { BrandedLoadingOverlay } from '../../../components/loader/branded-loader.tsx';
import { formatDate } from '../../../helpers/date-time-helpers/formatting/formatting.ts';
import { exportChart } from '../../../helpers/image-downloader/image-downloader';
import { useGlobalStore } from '../../../store/global-store/global-store.ts';
import { useWorkPeriodStore } from '../../../store/work-period-store/work-period-store.ts';
import { skyDark } from '../../../styles/design-tokens.ts';
import { Select } from '../../../ui-library/select/select.tsx';
import { Text } from '../../../ui-library/typography/typography.tsx';
import { FloraButton } from '../../flora/flora-button.tsx';
import type { TaskRecord } from '../../tasks-table/tasks-table.type.ts';
import { WorkPeriodChartHeader } from '../components/work-period-chart-header.tsx';
import {
  formatDeliveryConfidenceDataForFlora,
  getDeliveryConfidenceFloraPromptKey,
} from '../helpers/single-assessment-flora.helpers.ts';
import { useBurnsData, useWorkPeriodsTasksData } from '../hooks/work-periods-calculation.hooks.ts';
import { DeliveryConfidenceChart } from './delivery-confidence-chart.tsx';
import { CHART_TYPE_LABELS, ChartType } from './delivery-confidence.type.ts';
import {
  filterDeliveryData,
  filterHistoricalBurnsData,
  mergeDeliveryDataWithHistoricalBurnsData,
  transformForDeliveryChart,
  transformHistoricalBurnsData,
} from './delivery-section.helpers.ts';

type DeliveryConfidenceProps = {
  onTasksChange: (tasks: TaskRecord[], title?: string, isLoading?: boolean) => void;
};

export const DeliveryConfidence = ({ onTasksChange }: DeliveryConfidenceProps) => {
  const i18n = useTranslation();
  const downloadRef = useRef<HTMLDivElement>(null!);
  const { selectedMetric, selectedWorkPeriods } = useWorkPeriodStore();
  const selectedWorkPeriod = useMemo(() => selectedWorkPeriods[0], [selectedWorkPeriods]);
  const [chartType, setChartType] = useState(ChartType.BURN_UP);
  const [hoveredDate, setHoveredDate] = useState<string | null>(null);
  const useAI = useGlobalStore((state) => state.useAI);

  const { data, isLoading } = useBurnsData();
  const filteredDeliveryData = filterDeliveryData(data?.burns, selectedMetric);
  const deliveryData = transformForDeliveryChart(filteredDeliveryData, selectedMetric);

  const filteredHistoricalBurnsData = filterHistoricalBurnsData(data?.historical_burns, selectedMetric);
  const historicalBurnsData = transformHistoricalBurnsData(filteredHistoricalBurnsData, selectedMetric);

  const deliveryConfidenceChartData = useMemo(() => {
    return mergeDeliveryDataWithHistoricalBurnsData(deliveryData, historicalBurnsData);
  }, [deliveryData, historicalBurnsData]);

  const floraData = useMemo(
    () => formatDeliveryConfidenceDataForFlora(deliveryConfidenceChartData, selectedMetric, selectedWorkPeriod, i18n.t),
    [deliveryConfidenceChartData, selectedMetric, selectedWorkPeriod, i18n.t],
  );

  const floraPromptKey = useMemo(() => getDeliveryConfidenceFloraPromptKey(chartType), [chartType]);

  // handle tasks
  const [taskQuery, setTaskQuery] = useState<{
    filter: string;
    workPeriodId: string;
  } | null>(null);

  const { data: tasksQueryData = [], isFetching: isTasksFetching } = useWorkPeriodsTasksData(
    [Measure.FlowByPace],
    taskQuery?.filter,
    { enabled: !!taskQuery },
  );

  // Effect to trigger onTasksChange when tasks are loading
  useEffect(() => {
    if (taskQuery && isTasksFetching && onTasksChange) {
      const title = `Done - ${formatDate(taskQuery.filter.split(',')[1], { format: 'MMM D, YYYY' })}`;
      onTasksChange([], title, true);
    }
  }, [taskQuery, isTasksFetching, onTasksChange]);

  // Effect to trigger onTasksChange when tasks data is loaded
  useEffect(() => {
    if (taskQuery && !isTasksFetching && onTasksChange) {
      const title = `Done - ${formatDate(taskQuery.filter.split(',')[1], { format: 'MMM D, YYYY' })}`;
      onTasksChange(tasksQueryData as TaskRecord[], title, false);
    }
  }, [tasksQueryData, taskQuery, isTasksFetching, onTasksChange]);

  const handleDoneClick = () => {
    if (!hoveredDate) return;
    setTaskQuery({
      filter: `done,${hoveredDate}`,
      workPeriodId: selectedWorkPeriod?.id || '',
    });
  };

  const handleExport = () => {
    if (!downloadRef.current) return;
    exportChart(downloadRef.current, `delivery-confidence.png`);
  };
  const handleChartMouseMove = (data: any) => {
    if (data && data.activeLabel) {
      const normalizedDate = formatDate(data.activeLabel, { format: 'YYYY-MM-DD' });
      setHoveredDate(normalizedDate);
    }
  };

  return (
    <div ref={downloadRef} style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <WorkPeriodChartHeader
        title="Delivery Confidence"
        selectedWorkPeriod={selectedWorkPeriod}
        onExport={handleExport}
        floraButton={
          useAI && (
            <FloraButton
              externalData={[floraData]}
              prompt={i18n.t(floraPromptKey)}
              tooltipText={i18n.t('flora.work_periods.flora_button_tooltip')}
            />
          )
        }
      />
      <Group justify="space-between" align="center" mt={16} mb={16}>
        <Text weight="bold" color={skyDark}>
          {hoveredDate ? formatDate(hoveredDate, { format: 'MMM D, YYYY' }) : 'Delivery Confidence'}
        </Text>
        <Select
          value={chartType}
          options={[
            { value: ChartType.BURN_DOWN, label: CHART_TYPE_LABELS[ChartType.BURN_DOWN] },
            { value: ChartType.BURN_UP, label: CHART_TYPE_LABELS[ChartType.BURN_UP] },
          ]}
          onChange={(value) => setChartType(value as ChartType)}
          size="md"
          radius="xl"
        />
      </Group>
      <ChartScope>
        <BrandedLoadingOverlay variant="colored" visible={isLoading} scoped />
        <DeliveryConfidenceChart
          data={deliveryConfidenceChartData}
          chartType={chartType}
          handleChartMouseMove={handleChartMouseMove}
          handleDoneClick={handleDoneClick}
        />
      </ChartScope>
    </div>
  );
};

const ChartScope = styled.div`
  position: relative;
  flex: 1;
  min-height: 0;
`;
